import { useEffect, useState } from "react";
import { crewOpsApi } from "@/api";
import { RefreshCw, CheckCircle, AlertTriangle, Star } from "lucide-react";

const eventTypeLabels = {
  sickness: { label: "Sick Call", color: "text-red-400 bg-red-400/10 border-red-400/20" },
  aog: { label: "AOG", color: "text-red-400 bg-red-900/20 border-red-500/40" },
  weather: { label: "Weather", color: "text-blue-400 bg-blue-400/10 border-blue-400/20" },
  crew_shortage: { label: "Crew Shortage", color: "text-orange-400 bg-orange-400/10 border-orange-400/20" },
};

function RecoveryOptionsPanel({ caseId, onAction }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    crewOpsApi.getRecoveryOptions(caseId).then(r => setOptions(r.data)).finally(() => setLoading(false));
  }, [caseId]);

  const applyAction = async () => {
    if (!selected) return;
    setActioning(true);
    await crewOpsApi.takeRecoveryAction({ recovery_case_id: caseId, option_id: selected, notes: "Applied via Recovery Console" });
    setActioning(false);
    onAction();
  };

  if (loading) return <div className="text-xs text-muted-foreground py-2">Loading recovery options...</div>;

  return (
    <div className="mt-4 space-y-3">
      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Recovery Options — Ranked by Recommendation Score</div>
      {options.map(opt => (
        <div key={opt.id} onClick={() => setSelected(opt.id)}
          className={`p-3 border rounded-sm cursor-pointer transition-colors ${selected === opt.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/30 bg-card"}`}
          data-testid={`recovery-option-rank-${opt.option_rank}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">#{opt.option_rank}</span>
              {opt.recommendation_score >= 85 && <Star size={11} className="text-amber-400 fill-amber-400" />}
              <div className="text-xs font-medium text-foreground">{opt.description}</div>
            </div>
            <div className={`font-mono text-sm font-medium shrink-0 ${opt.recommendation_score >= 85 ? "text-green-400" : opt.recommendation_score >= 65 ? "text-amber-400" : "text-red-400"}`}>
              {opt.recommendation_score}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[["Fatigue Impact", opt.fatigue_impact_score, opt.fatigue_impact_score < 40 ? "text-green-400" : opt.fatigue_impact_score < 65 ? "text-amber-400" : "text-red-400"],
              ["Delay", opt.delay_impact_minutes > 0 ? `+${opt.delay_impact_minutes}min` : "None", opt.delay_impact_minutes > 60 ? "text-red-400" : "text-green-400"],
              ["Legality", opt.legality_status, "text-foreground"]].map(([lbl, val, col]) => (
              <div key={lbl} className="text-xs">
                <div className="text-muted-foreground">{lbl}</div>
                <div className={`font-mono font-medium ${col}`}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {selected && (
        <button onClick={applyAction} disabled={actioning}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-sm text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
          data-testid="apply-recovery-action">
          {actioning ? "Applying..." : `Apply Recovery Option #${options.find(o => o.id === selected)?.option_rank}`}
        </button>
      )}
    </div>
  );
}

export default function RecoveryConsole() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchCases = () => {
    crewOpsApi.getRecoveryCases().then(r => setCases(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchCases(); }, []);

  const open = cases.filter(c => c.status === "open");
  const resolved = cases.filter(c => c.status === "resolved");

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="recovery-console">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Crew Rostering · Module D</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Disruption Recovery</h1>
        <p className="text-xs text-muted-foreground mt-1">Rule-based recovery options ranked by safety, legality and crew fatigue impact</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Open Cases", open.length, "text-red-400"], ["Resolved", resolved.length, "text-green-400"], ["Total", cases.length, "text-primary"]].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-4">
            <div className={`text-3xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{lbl}</div>
          </div>
        ))}
      </div>

      {open.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Active Recovery Cases
          </div>
          {open.map(c => {
            const tc = eventTypeLabels[c.event_type] || { label: c.event_type, color: "text-muted-foreground border-border bg-muted/20" };
            return (
              <div key={c.id} className="bg-card border border-red-500/20 border-l-4 border-l-red-500 rounded-sm overflow-hidden"
                data-testid={`recovery-case-${c.id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold border ${tc.color}`}>{tc.label}</span>
                        <span className="text-xs text-red-400 font-bold uppercase">OPEN</span>
                      </div>
                      <h3 className="font-semibold text-foreground font-heading">{c.trigger_event}</h3>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{c.description}</p>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Affected Pairing: </span>{c.affected_pairing} ·
                    <span className="font-medium text-foreground ml-2">Crew: </span>{c.affected_crew}
                  </div>
                  <button onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                    className="mt-3 text-xs text-primary hover:underline flex items-center gap-1"
                    data-testid={`expand-recovery-${c.id}`}>
                    {expandedId === c.id ? "Hide" : "View"} Recovery Options
                  </button>
                </div>
                {expandedId === c.id && (
                  <div className="px-4 pb-4">
                    <RecoveryOptionsPanel caseId={c.id} onAction={fetchCases} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {resolved.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Resolved Cases</div>
          {resolved.map(c => (
            <div key={c.id} className="bg-card border border-border rounded-sm p-3 flex items-center gap-4">
              <CheckCircle size={14} className="text-green-400 shrink-0" />
              <div className="flex-1">
                <div className="text-xs font-medium text-foreground">{c.trigger_event}</div>
                <div className="text-xs text-muted-foreground">{c.case_code}</div>
              </div>
              <span className="text-xs text-green-400 font-bold">RESOLVED</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
